import React, { useState, useEffect } from "react";
import useUsers from "../../hooks/utilisateur/useUsers";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FiCamera, FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import "../../assets/css/profile.css";

const ROLE_BADGES = {
  1: { label: "Admin", className: "bg-primary" },
  2: { label: "Superviseur", className: "bg-warning text-dark" },
};

function Profile() {
  const [photoPreview, setPhotoPreview] = useState(null);
  const [user, setUser] = useState({
    nom: "",
    prenom: "",
    role: "",
    email: "",
    tel: "",
    adresse: "",
    photo: "",
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  const { updateUser, updatePDP } = useUsers();

  const urlProfile = process.env.REACT_APP_API_URL_PROFILE || "";

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));

    if (userData) {
      setUser({
        nom: userData.nom || "",
        prenom: userData.prenom || "",
        role: userData.role || "",
        email: userData.email || "",
        tel: userData.tel || "",
        adresse: userData.adresse || "",
        photo: userData.photo || "",
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setPhotoPreview(URL.createObjectURL(file));
    setPhotoFile(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    confirmAlert({
      title: "Confirmation",
      message: "Êtes-vous sûr de vouloir mettre à jour votre profil ?",
      buttons: [
        {
          label: "Oui",
          onClick: async () => {
            const storedUser = JSON.parse(localStorage.getItem("user"));
            const userId = storedUser?._id;

            if (!userId) {
              setMessage({
                type: "error",
                text: "Utilisateur introuvable.",
              });
              return;
            }

            try {
              const dataToUpdate = {
                nom: user.nom,
                prenom: user.prenom,
                email: user.email,
                tel: user.tel,
                adresse: user.adresse,
              };

              await updateUser(userId, dataToUpdate, (res) => {
                const merged = { ...storedUser, ...dataToUpdate, ...res };
                localStorage.setItem("user", JSON.stringify(merged));
              });

              if (photoFile) {
                await updatePDP(userId, photoFile, (res) => {
                  localStorage.setItem("user", JSON.stringify(res));
                  setUser({ ...res });
                  setPhotoFile(null);
                });
              }

              setMessage({
                type: "success",
                text: "Profil mis à jour avec succès !",
              });
              setTimeout(() => setMessage({ type: "", text: "" }), 2500);
            } catch (error) {
              console.error(error);
              setMessage({
                type: "error",
                text: "Erreur lors de la mise à jour du profil.",
              });
            }
          },
        },
        {
          label: "Non",
          onClick: () => {},
        },
      ],
    });
  };

  const roleBadge = ROLE_BADGES[Number(user.role)] || {
    label: "Utilisateur",
    className: "bg-secondary",
  };

  const avatarSrc = photoPreview
    ? photoPreview
    : user.photo
      ? `${urlProfile}${user.photo}`
      : "/images/layout_img/user_img.jpg";

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">Profil</h2>
      </div>

      {/* ALERT MESSAGE */}
      {message.text && (
        <div
          className={`alert ${
            message.type === "success" ? "alert-success" : "alert-danger"
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <div className="row g-4">
        {/* ---- RÉSUMÉ ---- */}
        <div className="col-lg-4">
          <div className="card shadow-sm h-100">
            <div className="card-body text-center py-4">
              <label htmlFor="photo-upload" className="profile-avatar-wrap">
                <img
                  src={avatarSrc}
                  alt="Profil"
                  className="profile-avatar rounded-circle"
                />
                <span className="profile-avatar-edit">
                  <FiCamera />
                </span>
              </label>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handlePhotoChange}
              />

              <h4 className="mt-3 mb-1">
                {user.nom} {user.prenom}
              </h4>

              <span className={`badge rounded-pill ${roleBadge.className}`}>
                {roleBadge.label}
              </span>

              <hr className="my-4" />

              <ul className="list-unstyled text-start d-flex flex-column gap-3 mb-0">
                <li className="d-flex align-items-center gap-2">
                  <FiMail className="text-primary flex-shrink-0" />
                  <span className="text-truncate">{user.email || "—"}</span>
                </li>
                <li className="d-flex align-items-center gap-2">
                  <FiPhone className="text-success flex-shrink-0" />
                  <span>{user.tel || "—"}</span>
                </li>
                <li className="d-flex align-items-center gap-2">
                  <FiMapPin className="text-warning flex-shrink-0" />
                  <span className="text-truncate">{user.adresse || "—"}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ---- ÉDITION ---- */}
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h3 className="h6 mb-0">Modifier les informations</h3>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Nom</label>
                    <input
                      type="text"
                      name="nom"
                      className="form-control"
                      value={user.nom}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Prénom</label>
                    <input
                      type="text"
                      name="prenom"
                      className="form-control"
                      value={user.prenom}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      value={user.email}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Téléphone</label>
                    <input
                      type="text"
                      name="tel"
                      className="form-control"
                      value={user.tel}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Adresse</label>
                    <input
                      type="text"
                      name="adresse"
                      className="form-control"
                      value={user.adresse}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button type="submit" className="btn btn-primary px-4">
                    Mettre à jour
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
